import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError, map, Observable } from 'rxjs';
import { CustomerDTO } from '../DTOs/CustomerDTO';
import { CountryDTO } from '../DTOs/CountryDTO';
import { FactoryDTO } from '../DTOs/FactoryDTO';
import { LaboratoryDTO } from '../DTOs/LaboratoryDTO';
import { ImporteurDTO } from '../DTOs/ImporteurDTO';
import { ArticleSizeDTO } from '../DTOs/ArticleSizeDTO';
import { BaseInspectionOverview } from '../DTOs/InspectionMasterData/productDetailModel';
import { ArticleTypeDTO, ArticleTypeImageDTO, InspectionArticlePOMDTO } from '../DTOs/ArticleTypeDTO';
import { InspectionTypeDetailDTO, InspectionTypeOverviewResponse } from '../DTOs/InspectionMasterData/InspectionType';
import { InspectionOverviewDTO } from '../DTOs/InspectionOverviewDTO';
import { InspectionDetailDTO } from '../DTOs/InspectionDetailDTO';
import { InspectionLevelDTO } from '../DTOs/InspectionLevelDTO';
import { ContactPersonDTO } from '../DTOs/ContactPersonDTO';
import { ExecuteInspectionDTO } from '../DTOs/ExecuteInspectionDTO';
import { environment } from 'src/environments/environment';
import { CreateDefectCommand, DefectListItemDTO, GridResultDto, UpdateDefectsCommand } from '../DTOs/DefectDTO';
import { ISBaseDTO } from '../DTOs/ISBaseDTO';

export interface InspectionImageUpdateResponse {
    imageId: number;
    newUrl: string;
    version: number;
}

@Injectable({ providedIn: 'root' })
export class BaseService {
    private readonly baseUrl = `${environment.backendUrl}/MasterData`;
    private readonly inspectionMasterdataURL = `${environment.backendUrl}/InspectionMasterData`;
    private readonly inspectionURL = `${environment.backendUrl}/Inspection`;

    constructor(private http: HttpClient) { }

    // ============ CUSTOMERS ============
    getCustomers(
        customerId?: number | null,
        active?: boolean | null,
        search?: string | null
    ) {
        let params = new HttpParams();

        if (customerId != null) params = params.set('customerId', String(customerId));
        if (active != null) params = params.set('active', String(active));
        if (search) params = params.set('search', search);

        return this.http.get<CustomerDTO[]>(
            `${this.baseUrl}/CustomerOverview`,
            params.keys().length ? { params } : {}
        );
    }

    createCustomer(customer: CustomerDTO): Observable<number> {
        return this.http.post<number>(`${this.baseUrl}/CreateCustomer`, customer);
    }

    updateCustomer(customer: CustomerDTO): Observable<void> {
        return this.http.put<void>(`${this.baseUrl}/UpdateCustomerData`, customer);
    }

    deleteCustomer(id: number): Observable<boolean> {
        return this.http.delete<boolean>(`${this.baseUrl}/DeleteCustomer?id=${id}`);
    }

    // ============ FACTORIES ============
    getFactories(
        factoryId?: number | null,
        active?: boolean | null,
        search?: string | null
    ) {
        let params = new HttpParams();

        if (factoryId != null) params = params.set('factoryId', String(factoryId));
        if (active != null) params = params.set('active', String(active));
        if (search) params = params.set('search', search);

        return this.http.get<FactoryDTO[]>(
            `${this.baseUrl}/FactoryOverview`,
            params.keys().length ? { params } : {}
        );
    }

    createFactory(factory: FactoryDTO): Observable<number> {
        return this.http.post<number>(`${this.baseUrl}/CreateFactory`, factory);
    }

    updateFactory(factory: FactoryDTO): Observable<void> {
        return this.http.put<void>(`${this.baseUrl}/UpdateFactory`, factory);
    }

    deleteFactory(id: number): Observable<boolean> {
        return this.http.delete<boolean>(`${this.baseUrl}/DeleteFactory?id=${id}`);
    }

    // ============ IMPORTEURS ============
    getImporteurs(
        importeurId?: number | null,
        active?: boolean | null,
        search?: string | null
    ) {
        let params = new HttpParams();

        if (importeurId != null) params = params.set('importeurId', String(importeurId));
        if (active != null) params = params.set('active', String(active));
        if (search) params = params.set('search', search);

        return this.http.get<ImporteurDTO[]>(
            `${this.baseUrl}/ImporteurOverview`,
            params.keys().length ? { params } : {}
        );
    }

    createImporteur(importeur: ImporteurDTO): Observable<number> {
        return this.http.post<number>(`${this.baseUrl}/CreateImporteur`, importeur);
    }

    updateImporteur(importeur: ImporteurDTO): Observable<void> {
        return this.http.put<void>(`${this.baseUrl}/UpdateImporteur`, importeur);
    }

    deleteImporteur(id: number): Observable<boolean> {
        return this.http.delete<boolean>(`${this.baseUrl}/DeleteImporteur?id=${id}`);
    }

    // ============ LABORATORIES ============
    getLaboratories(id?: number): Observable<LaboratoryDTO[]> {
        const params = id
            ? new HttpParams().set('LaboratoryId', id.toString())
            : new HttpParams();

        return this.http.get<LaboratoryDTO[]>(
            `${this.baseUrl}/LaboratoryOverview`,
            { params }
        );
    }

    createLaboratory(laboratory: LaboratoryDTO): Observable<number> {
        return this.http.post<number>(
            `${this.baseUrl}/CreateLaboratory`,
            laboratory
        );
    }

    updateLaboratory(laboratory: LaboratoryDTO): Observable<boolean> {
        return this.http.put<boolean>(
            `${this.baseUrl}/UpdateLaboratory`,
            laboratory
        );
    }

    deleteLaboratory(id: number): Observable<boolean> {
        const params = new HttpParams().set('id', id.toString());
        return this.http.delete<boolean>(
            `${this.baseUrl}/DeleteLaboratory`,
            { params }
        );
    }

    // ============ COUNTRIES ============
    GetAllCountrys(): Observable<CountryDTO[]> {
        return this.http.get<CountryDTO[]>(`${this.baseUrl}/AllCountrys`);
    }

    // ============ ARTICLE SIZES ============
    getArticleSizes(id?: number): Observable<ArticleSizeDTO[]> {
        const params = id ? new HttpParams().set('id', id.toString()) : new HttpParams();
        return this.http.get<ArticleSizeDTO[]>(
            `${this.baseUrl}/ArticleSizeOverview`,
            { params }
        );
    }

    createArticleSize(articleSize: ArticleSizeDTO): Observable<number> {
        return this.http.post<number>(`${this.baseUrl}/CreateArticleSize`, articleSize);
    }

    updateArticleSize(articleSize: ArticleSizeDTO): Observable<boolean> {
        return this.http.put<boolean>(
            `${this.baseUrl}/UpdateArticleSize`,
            articleSize
        );
    }

    deleteArticleSize(id: number): Observable<boolean> {
        const params = new HttpParams().set('id', id.toString());
        return this.http.delete<boolean>(
            `${this.baseUrl}/DeleteArticleSize`,
            { params }
        );
    }

    // ============ ARTICLE TYPES ============
    getArticleTypes(): Observable<ArticleTypeDTO[]> {
        return this.http.get<ArticleTypeDTO[]>(`${this.baseUrl}/ArticleTypeOverview`);
    }

    getArticleTypeDetail(id: number): Observable<ArticleTypeDTO> {
        return this.http.get<ArticleTypeDTO>(`${this.baseUrl}/ArticleTypeDetail?id=${id}`);
    }

    updateArticleType(articleType: ArticleTypeDTO): Observable<boolean> {
        return this.http.put<boolean>(`${this.baseUrl}/UpdateArticleType`, articleType);
    }

    createArticleType(articleType: ArticleTypeDTO): Observable<number> {
        return this.http.post<number>(`${this.baseUrl}/CreateArticleType`, { articleType });
    }

    deleteArticleType(id: number): Observable<boolean> {
        return this.http.delete<boolean>(`${this.baseUrl}/DeleteArticleType?id=${id}`);
    }


    uploadArticleTypeImage(articleTypeId: number, file: File, isPrimary = false): Observable<ArticleTypeImageDTO> {
        const formData = new FormData();
        formData.append('file', file, file.name);
        formData.append('isPrimary', String(isPrimary));

        return this.http.post<ArticleTypeImageDTO>(
            `${this.baseUrl}/UploadImage/articletypes/${articleTypeId}/image`,
            formData
        );
    }

    deleteArticleTypeImage(articleTypeId: number, imageId: number): Observable<boolean> {
        return this.http.delete<boolean>(
            `${this.baseUrl}/DeleteArticleTypeImage/articletypes/${articleTypeId}/images/${imageId}`
        );
    }

    setArticleTypePrimaryImage(articleTypeId: number, imageId: number): Observable<boolean> {
        return this.http.put<boolean>(
            `${this.baseUrl}/SetArticleTypePrimaryImage/articletypes/${articleTypeId}/images/${imageId}/primary`,
            {}
        );
    }

    // ===================== DEFFECTS =====================

    getDefects(
        customerId: number,
        parentId: number | null,
        search: string | null,
        skip: number,
        take: number
    ): Observable<GridResultDto<DefectListItemDTO>> {

        let params = new HttpParams()
            .set('customerId', customerId.toString())
            .set('skip', skip.toString())
            .set('take', take.toString());

        if (parentId !== null) {
            params = params.set('parentId', parentId.toString());
        }

        if (search && search.trim().length > 0) {
            params = params.set('search', search.trim());
        }

        return this.http.get<GridResultDto<DefectListItemDTO>>(
            `${this.inspectionMasterdataURL}/DefectsOverview`,
            { params }
        );
    }

    createDefect(command: CreateDefectCommand): Observable<number> {
        return this.http.post<number>(
            `${this.inspectionMasterdataURL}/CreateDefect`,
            command
        );
    }

    updateDefect(command: UpdateDefectsCommand): Observable<number> {
        return this.http.put<number>(
            `${this.inspectionMasterdataURL}/UpdateDefect/${command.defectId}`,
            command
        );
    }

    deleteDefect(defectId: number): Observable<number> {
        return this.http.delete<number>(
            `${this.inspectionMasterdataURL}/DeleteDefect/${defectId}`
        );
    }

    // ============ PRODUCT DETAILS ============
    getProductDetailsOverview(): Observable<BaseInspectionOverview[]> {
        return this.http.get<BaseInspectionOverview[]>(`${this.inspectionMasterdataURL}/ProductDetailsOverview`);
    }

    createProductDetail(dto: BaseInspectionOverview): Observable<BaseInspectionOverview> {
        return this.http.post<BaseInspectionOverview>(`${this.inspectionMasterdataURL}/CreateProductDetail`, dto);
    }

    updateProductDetail(dto: BaseInspectionOverview): Observable<void> {
        return this.http.put<void>(`${this.inspectionMasterdataURL}/UpdateProductDetail/${dto.databaseId}`, dto);
    }

    deleteProductDetail(id: number): Observable<boolean> {
        return this.http.delete<boolean>(`${this.inspectionMasterdataURL}/DeleteProductDetail/${id}`);
    }

    // ============ Requirements ============
    getRequirementsOverview(): Observable<BaseInspectionOverview[]> {
        return this.http.get<BaseInspectionOverview[]>(`${this.inspectionMasterdataURL}/RequirementsOverview`);
    }

    createRequirements(dto: BaseInspectionOverview): Observable<BaseInspectionOverview> {
        return this.http.post<BaseInspectionOverview>(`${this.inspectionMasterdataURL}/CreateRequirement`, dto);
    }

    updateRequirements(databaseId: number, dto: BaseInspectionOverview): Observable<void> {
        return this.http.put<void>(`${this.inspectionMasterdataURL}/UpdateRequirement/${databaseId}`, dto);
    }

    deleteRequirement(id: number): Observable<boolean> {
        return this.http.delete<boolean>(`${this.inspectionMasterdataURL}/DeleteRequirement/${id}`);
    }

    // ============ Packaging ============
    getPackagingOverview(): Observable<BaseInspectionOverview[]> {
        return this.http.get<BaseInspectionOverview[]>(`${this.inspectionMasterdataURL}/PackingOverview`);
    }

    createPackaging(dto: BaseInspectionOverview): Observable<BaseInspectionOverview> {
        return this.http.post<BaseInspectionOverview>(`${this.inspectionMasterdataURL}/CreatePacking`, dto);
    }

    updatePackaging(databaseId: number, dto: BaseInspectionOverview): Observable<void> {
        return this.http.put<void>(`${this.inspectionMasterdataURL}/UpdatePacking/${databaseId}`, dto);
    }

    deletePacking(id: number): Observable<boolean> {
        return this.http.delete<boolean>(`${this.inspectionMasterdataURL}/DeletePacking/${id}`);
    }

    // ============ Documents ============
    getDocumentsOverview(): Observable<BaseInspectionOverview[]> {
        return this.http.get<BaseInspectionOverview[]>(`${this.inspectionMasterdataURL}/DocumentsOverview`);
    }

    createDocuments(dto: BaseInspectionOverview): Observable<BaseInspectionOverview> {
        return this.http.post<BaseInspectionOverview>(`${this.inspectionMasterdataURL}/CreateDocument`, dto);
    }

    updateDocuments(databaseId: number, dto: BaseInspectionOverview): Observable<void> {
        return this.http.put<void>(`${this.inspectionMasterdataURL}/UpdateDocument/${databaseId}`, dto);
    }

    deleteDocument(id: number): Observable<boolean> {
        return this.http.delete<boolean>(`${this.inspectionMasterdataURL}/DeleteDocument/${id}`);
    }

    // ============ Identical Design ============
    getIdenticalDesignOverview(): Observable<BaseInspectionOverview[]> {
        return this.http.get<BaseInspectionOverview[]>(`${this.inspectionMasterdataURL}/IdenticalDesignOverview`);
    }

    createIdenticalDesign(dto: BaseInspectionOverview): Observable<BaseInspectionOverview> {
        return this.http.post<BaseInspectionOverview>(`${this.inspectionMasterdataURL}/CreateIdenticalDesign`, dto);
    }

    updateIdenticalDesign(databaseId: number, dto: BaseInspectionOverview): Observable<void> {
        return this.http.put<void>(`${this.inspectionMasterdataURL}/UpdateIdenticalDesign/${databaseId}`, dto);
    }

    deleteIdenticalDesign(id: number): Observable<boolean> {
        return this.http.delete<boolean>(`${this.inspectionMasterdataURL}/DeleteIdenticalDesign/${id}`);
    }


    // ============ Inspection Type ============
    getInspectionTypeOverview(): Observable<InspectionTypeOverviewResponse> {
        return this.http.get<InspectionTypeOverviewResponse>(
            `${this.baseUrl}/InspectionTypeOverview`
        ).pipe(
            map(response => {
                return {
                    customers: response.customers,
                    inspectionTypes: response.inspectionTypes.map(type => ({
                        ...type,
                        auditTypeId: type.inspectionTypeId
                    } as InspectionTypeDetailDTO))
                };
            })
        );
    }

    getInspectionTypeDetail(id: number): Observable<InspectionTypeDetailDTO> {
        return this.http.get<InspectionTypeDetailDTO>(`${this.baseUrl}/InspectionTypeDetail?id=${id}`);
    }

    createInspectionType(dto: InspectionTypeDetailDTO): Observable<number> {
        return this.http.post<number>(`${this.baseUrl}/CreateInspectionType`, dto);
    }

    updateInspectionType(databaseId: number, dto: InspectionTypeDetailDTO): Observable<void> {
        console.log(dto);
        return this.http.put<void>(`${this.baseUrl}/UpdateInspectionType/`, dto);
    }

    deleteInspectionType(id: number): Observable<boolean> {
        return this.http.delete<boolean>(`${this.baseUrl}/DeleteInspectionType/${id}`);
    }

    // ============= Inspections Overview =================
    getInspectionOverview(): Observable<InspectionOverviewDTO[]> {
        return this.http.get<InspectionOverviewDTO[]>(`${this.inspectionURL}/InspectionOverview`);
    }

    // ============= Inspections Detail =================
    getInspectionDetail(
        inspectionId?: number | null,
        active?: boolean | null,
        search?: string | null
    ): Observable<InspectionDetailDTO> {
        let params = new HttpParams();

        if (inspectionId != null && inspectionId > 0) params = params.set('id', String(inspectionId));
        if (active != null) params = params.set('active', String(active));
        if (search) params = params.set('search', search);
        return this.http.get<any>(
            `${this.inspectionURL}/InspectionDetail`,
            params.keys().length ? { params } : {}
        ).pipe(
            map((inspection) => this.mapInspectionDetailResponse(inspection))
        );
    }

    createInspection(inspection: InspectionDetailDTO): Observable<number> {
        const command = { dto: this.mapInspectionDetailPayload(inspection) };
        return this.http.post<number>(`${this.inspectionURL}/CreateInspection`, command);
    }

    updateInspection(inspection: InspectionDetailDTO): Observable<boolean> {
        const command = { dto: this.mapInspectionDetailPayload(inspection) };
        return this.http.put<boolean>(`${this.inspectionURL}/UpdateInspection`, command);
    }

    getPomsByArticleType(articleTypeId: number) {
        return this.http.get<InspectionArticlePOMDTO[]>(`${this.inspectionURL}/GetPomsByArticleType/${articleTypeId}`);
    }

    deleteInspection(id: number): Observable<boolean> {
        return this.http.delete<boolean>(`${this.inspectionURL}/DeleteInspection?id=${id}`);
    }


    //---------------------------------------------------------------------------

    getExecuteInspection(id: number): Observable<ExecuteInspectionDTO> {
        const params = new HttpParams().set('id', String(id));
        return this.http.get<ExecuteInspectionDTO>(
            `${this.inspectionURL}/GetExecuteInspection`,
            { params }
        );
    }

    updateExecuteInspectionBase(inspectionStepTypeId: number, baseDto: ISBaseDTO): Observable<boolean> {
        return this.http.put<boolean>(`${this.inspectionURL}/UpdateExecuteInspectionBase`, {
            inspectionStepTypeId,
            baseDto
        });
    }

    private mapInspectionDetailResponse(inspection: any): InspectionDetailDTO {
        return {
            ...inspection,
            inspectionArticles: inspection?.inspectionArticles ?? inspection?.articleList ?? [],
        } as InspectionDetailDTO;
    }

    private mapInspectionDetailPayload(inspection: InspectionDetailDTO): any {
        return {
            ...inspection,
            articleList: inspection.inspectionArticles ?? [],
        };
    }

    updateExecuteInspectionMeasurements(inspectionArticlePOMSizeId: number, measurements: {
        sorting: number;
        measurement: number | null;
    }[]): Observable<boolean> {
        return this.http.put<boolean>(`${this.inspectionURL}/UpdateExecuteInspectionMeasurements`, {
            inspectionArticlePOMSizeId,
            measurements,
        });
    }

    // --------------------------------------------------------------------------------------------
    // ---------------------------- Execute Inspection Image --------------------------------------
    // --------------------------------------------------------------------------------------------
    uploadInspectionStepDefectImage(payload: {
        inspectionId: number;
        inspectionStepId: number;
        inspectionStepTitle: string;
        itemId: number;
        itemTitle: string;
        file: File;
    }): Observable<string> {
        const formData = new FormData();

        formData.append('inspectionId', payload.inspectionId.toString());
        formData.append('inspectionStepId', payload.inspectionStepId.toString());
        formData.append('inspectionStepTitle', payload.inspectionStepTitle);
        formData.append('itemId', payload.itemId.toString());
        formData.append('itemTitle', payload.itemTitle);
        formData.append('file', payload.file, payload.file.name);

        return this.http
            .put<{ imageUrl: string }>(
                `${this.inspectionURL}/InspectionStepDefect/image`,
                formData
            )
            .pipe(map(response => response.imageUrl));
    }

    uploadInspectionStepDocumentImage(payload: {
        inspectionId: number;
        inspectionStepId: number;
        inspectionStepTitle: string;
        itemId: number;
        itemTitle: string;
        file: File;
    }): Observable<string> {
        const formData = new FormData();

        formData.append('inspectionId', payload.inspectionId.toString());
        formData.append('inspectionStepId', payload.inspectionStepId.toString());
        formData.append('inspectionStepTitle', payload.inspectionStepTitle);
        formData.append('itemId', payload.itemId.toString());
        formData.append('itemTitle', payload.itemTitle);
        formData.append('file', payload.file, payload.file.name);

        return this.http
            .put<{ imageUrl: string }>(
                `${this.inspectionURL}/UploadInspectionStepDocumentImage/InspectionStepDocument/image`,
                formData
            )
            .pipe(map(response => response.imageUrl));
    }

    updateInspectionImage(payload: {
        entityType: string;
        entityId: number;
        file?: File | null;
        imageId?: number | null;
        comment?: string | null;
    }): Observable<InspectionImageUpdateResponse> {
        const formData = new FormData();

        formData.append('entityType', payload.entityType);
        formData.append('entityId', payload.entityId.toString());
        if (payload.imageId != null) {
            formData.append('imageId', payload.imageId.toString());
        }
        if (payload.comment !== undefined) {
            formData.append('comment', payload.comment ?? '');
        }
        if (payload.file) {
            formData.append('file', payload.file, payload.file.name);
        }

        return this.http.post<InspectionImageUpdateResponse>(
            `${this.inspectionURL}/UpdateImage`,
            formData
        );
    }

    deleteInspectionImage(payload: {
        entityType: string;
        entityId: number;
        imageId: number;
    }): Observable<boolean> {
        return this.http.post<boolean>(
            `${this.inspectionURL}/DeleteImage`,
            payload
        );
    }

    getInspectionImageBlob(imageUrl: string): Observable<Blob> {
        const blobName = this.extractBlobName(imageUrl);
        const params = new HttpParams().set('blobName', blobName);

        return this.http.get(`${this.inspectionURL}/ImageContent`, {
            params,
            responseType: 'blob'
        });
    }

    private extractBlobName(imageUrl: string): string {
        const url = new URL(imageUrl);
        const path = url.pathname;
        const filesSegment = '/files/';
        const segmentIndex = path.indexOf(filesSegment);

        if (segmentIndex < 0) {
            throw new Error('Unsupported inspection image url.');
        }

        return decodeURIComponent(path.substring(segmentIndex + filesSegment.length));
    }


    // ============= Contact Persons =================
    getContactPerson(id: number, area: string): Observable<ContactPersonDTO[]> {
        return this.http.get<ContactPersonDTO[]>(
            `${this.baseUrl}/Get${area}ContactPersons?id=${id}`
        )
    }
    // ============= Users =================
    getAllUsers(): Observable<any[]> {
        return this.http.get<any[]>(`${environment.backendUrl}/Users/GetAllUsers`);
    }
    getUser(userId: number): Observable<any> {
        return this.http.get<any>(`${environment.backendUrl}/Users/GetUserDetail/${userId}`);
    }
    createUser(userData: any): Observable<any> {
        return this.http.post(`${environment.backendUrl}/Users/CreateUser`, userData);
    }
    updateUser(userId: number, userData: any): Observable<any> {
        return this.http.put(`${environment.backendUrl}/Users/UpdateUser/${userId}`, userData);
    }
    deleteUser(userId: number): Observable<any> {
        return this.http.delete(`${environment.backendUrl}/Users/DeleteUser/${userId}`);
    }
    activateUser(userId: number): Observable<any> {
        return this.http.post(`${environment.backendUrl}/Users/ActivateUser/${userId}/activate`, {});
    }
    deactivateUser(userId: number): Observable<any> {
        return this.http.post(`${environment.backendUrl}/Users/DeactivateUser/${userId}/deactivate`, {});
    }
    resetMFA(userId: number): Observable<any> {
        return this.http.post(`${environment.backendUrl}/Users/ResetMFA/${userId}`, {});
    }
    resetPassword(userId: number, newPassword: string): Observable<any> {
        return this.http.post(`${environment.backendUrl}/Users/ResetPassword/${userId}`, { newPassword });
    }
    getRoles(): Observable<any[]> {
        return this.http.get<any[]>(`${environment.backendUrl}/Users/GetAllRoles`);
    }
}
